import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Crosshair } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEFAULT_CENTER = { lat: -3.4488, lng: -79.9595 };

const loadGoogleMaps = () => {
  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error('missing-google-maps-key'));
  }
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }
  if (window.__proviGoogleMapsPromise) {
    return window.__proviGoogleMapsPromise;
  }

  window.__proviGoogleMapsPromise = new Promise((resolve, reject) => {
    window.gm_authFailure = () => reject(new Error('google-maps-auth-failed'));
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&libraries=places&language=es&region=EC`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('google-maps-load-failed'));
    document.head.appendChild(script);
  });

  return window.__proviGoogleMapsPromise;
};

const toNumber = (value) => Number(value);
const hasPoint = (point) => Number.isFinite(toNumber(point?.lat)) && Number.isFinite(toNumber(point?.lng));
const asLatLng = (point) => ({ lat: toNumber(point.lat), lng: toNumber(point.lng) });

const normalizePlace = (place, fallbackName = 'Ubicacion seleccionada') => {
  const location = place?.geometry?.location;
  if (!location) return null;
  return {
    lat: location.lat(),
    lng: location.lng(),
    address: place.formatted_address || place.name || fallbackName,
    place_id: place.place_id,
    source: 'google_places'
  };
};

const haversineKm = (from, to) => {
  if (!hasPoint(from) || !hasPoint(to)) return 0;
  const a = asLatLng(from);
  const b = asLatLng(to);
  const toRad = (degrees) => degrees * Math.PI / 180;
  const earthKm = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const value = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return earthKm * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
};

const svgIcon = (kind, fill, stroke = '#FFFFFF') => {
  const body = kind === 'person'
    ? `<circle cx="24" cy="15" r="7" fill="${fill}" stroke="${stroke}" stroke-width="3"/><path d="M13 45c2-12 7-18 11-18s9 6 11 18" fill="${fill}" stroke="${stroke}" stroke-width="3" stroke-linecap="round"/>`
    : kind === 'car'
      ? `<path d="M10 31l4-11c1-3 4-5 7-5h14c3 0 6 2 7 5l4 11v11H10V31z" fill="${fill}" stroke="${stroke}" stroke-width="3"/><circle cx="18" cy="42" r="4" fill="${stroke}"/><circle cx="38" cy="42" r="4" fill="${stroke}"/>`
      : `<path d="M24 4c-9 0-16 7-16 16 0 12 16 28 16 28s16-16 16-28C40 11 33 4 24 4z" fill="${fill}" stroke="${stroke}" stroke-width="3"/><circle cx="24" cy="20" r="6" fill="${stroke}"/>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">${body}</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const buildMarkerIcon = (google, kind, fallbackColor, customUrl) => ({
  url: customUrl || svgIcon(kind, fallbackColor),
  scaledSize: new google.maps.Size(42, 42),
  anchor: new google.maps.Point(21, 42)
});

const GoogleTaxiMap = ({
  pickup,
  dropoff,
  onPickupChange,
  onDropoffChange,
  onRouteChange,
  activeTarget = 'dropoff',
  height = '340px',
  showInputs = true,
  onFallback,
  markerIcons = {},
  onActiveTargetChange,
  showLocationButton = true,
  readOnly = false
}) => {
  const mapRef = useRef(null);
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const geocoderRef = useRef(null);
  const placesServiceRef = useRef(null);
  const activeTargetRef = useRef(activeTarget);
  const routeRequestRef = useRef(0);
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState('');
  const [pickupText, setPickupText] = useState(pickup?.address || '');
  const [dropoffText, setDropoffText] = useState(dropoff?.address || '');

  const markerConfig = useMemo(() => ({
    pickup: markerIcons.pickup || markerIcons.taxi_client_marker_icon_url,
    dropoff: markerIcons.dropoff || markerIcons.taxi_destination_marker_icon_url,
    driver: markerIcons.driver || markerIcons.taxi_driver_marker_icon_url
  }), [markerIcons]);

  useEffect(() => {
    activeTargetRef.current = activeTarget;
  }, [activeTarget]);

  const setActiveTarget = useCallback((target) => {
    activeTargetRef.current = target;
    if (onActiveTargetChange) onActiveTargetChange(target);
  }, [onActiveTargetChange]);

  useEffect(() => {
    setPickupText(pickup?.address || '');
  }, [pickup?.address]);

  useEffect(() => {
    setDropoffText(dropoff?.address || '');
  }, [dropoff?.address]);

  const reverseGeocode = useCallback((latLng, basePoint, onChange) => {
    geocoderRef.current?.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        onChange({
          ...basePoint,
          address: results[0].formatted_address,
          place_id: results[0].place_id
        });
      }
    });
  }, []);

  const geocodeText = useCallback((target, text) => {
    const query = (text || '').trim();
    if (!query || !geocoderRef.current) return;
    setMessage('Buscando direccion...');
    geocoderRef.current.geocode(
      { address: query, componentRestrictions: { country: 'EC' }, region: 'EC' },
      (results, status) => {
        if (status !== 'OK' || !results?.[0]?.geometry?.location) {
          setMessage('No se encontro esa direccion. Puedes elegir una sugerencia o tocar el mapa.');
          return;
        }
        const next = normalizePlace(results[0], target === 'pickup' ? 'Punto de recogida' : 'Destino');
        if (target === 'pickup') onPickupChange(next);
        else onDropoffChange(next);
        setMessage('');
      }
    );
  }, [onPickupChange, onDropoffChange]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !mapRef.current) return;
        const center = hasPoint(pickup) ? asLatLng(pickup) : hasPoint(dropoff) ? asLatLng(dropoff) : DEFAULT_CENTER;
        const map = new google.maps.Map(mapRef.current, {
          center,
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: !readOnly,
          gestureHandling: readOnly ? 'none' : 'auto',
          draggable: !readOnly,
          scrollwheel: !readOnly,
          disableDoubleClickZoom: readOnly,
          keyboardShortcuts: !readOnly,
          disableDefaultUI: readOnly
        });
        mapInstanceRef.current = map;
        directionsServiceRef.current = new google.maps.DirectionsService();
        directionsRendererRef.current = new google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          preserveViewport: false,
          polylineOptions: {
            strokeColor: '#D946EF',
            strokeWeight: 7,
            strokeOpacity: 0.95
          }
        });
        geocoderRef.current = new google.maps.Geocoder();
        placesServiceRef.current = new google.maps.places.PlacesService(map);
        map.addListener('click', (event) => {
          if (readOnly) return;
          const point = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
            address: `${event.latLng.lat().toFixed(5)}, ${event.latLng.lng().toFixed(5)}`,
            source: 'google_map'
          };
          const target = activeTargetRef.current;
          const applyPoint = (nextPoint) => {
            if (target === 'pickup') onPickupChange(nextPoint);
            else onDropoffChange(nextPoint);
          };
          if (event.placeId && placesServiceRef.current) {
            event.stop?.();
            placesServiceRef.current.getDetails(
              { placeId: event.placeId, fields: ['place_id', 'geometry', 'formatted_address', 'name'] },
              (place, status) => {
                if (status === 'OK' && place?.geometry?.location) {
                  applyPoint({
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                    address: place.name || place.formatted_address || point.address,
                    place_id: place.place_id,
                    source: 'google_map'
                  });
                } else {
                  applyPoint(point);
                  reverseGeocode(event.latLng, point, applyPoint);
                }
              }
            );
            return;
          }
          if (target === 'pickup') onPickupChange(point);
          else onDropoffChange(point);
          reverseGeocode(event.latLng, point, target === 'pickup' ? onPickupChange : onDropoffChange);
        });
        setReady(true);
      })
      .catch(() => {
        setMessage('No se pudo cargar Google Maps. Se mantiene el mapa alternativo.');
        if (onFallback) onFallback();
      });

    return () => {
      cancelled = true;
    };
  }, [onFallback, onPickupChange, onDropoffChange, reverseGeocode, readOnly]);

  useEffect(() => {
    if (!ready || !window.google?.maps?.places || !pickupInputRef.current || !dropoffInputRef.current) return undefined;
    const google = window.google;
    const options = {
      fields: ['place_id', 'geometry', 'formatted_address', 'name'],
      componentRestrictions: { country: 'ec' }
    };
    const pickupAutocomplete = new google.maps.places.Autocomplete(pickupInputRef.current, options);
    const dropoffAutocomplete = new google.maps.places.Autocomplete(dropoffInputRef.current, options);
    const pickupListener = pickupAutocomplete.addListener('place_changed', () => {
      const next = normalizePlace(pickupAutocomplete.getPlace(), 'Punto de recogida');
      if (next) {
        onPickupChange(next);
        setMessage('');
      }
    });
    const dropoffListener = dropoffAutocomplete.addListener('place_changed', () => {
      const next = normalizePlace(dropoffAutocomplete.getPlace(), 'Destino');
      if (next) {
        onDropoffChange(next);
        setMessage('');
      }
    });
    return () => {
      google.maps.event.removeListener(pickupListener);
      google.maps.event.removeListener(dropoffListener);
    };
  }, [ready, onPickupChange, onDropoffChange]);

  useEffect(() => {
    if (!ready || !mapInstanceRef.current || !window.google?.maps) return;
    const google = window.google;
    const map = mapInstanceRef.current;

    const ensureMarker = (ref, point, label, kind, color, onChange, customUrl) => {
      if (!hasPoint(point)) {
        if (ref.current) {
          ref.current.setMap(null);
          ref.current = null;
        }
        return;
      }
      const position = asLatLng(point);
      if (!ref.current) {
        ref.current = new google.maps.Marker({
          map,
          position,
          label: {
            text: label,
            color: '#FFFFFF',
            fontWeight: '700',
            fontSize: '11px'
          },
          draggable: !readOnly,
          icon: buildMarkerIcon(google, kind, color, customUrl)
        });
        if (!readOnly) {
          ref.current.addListener('dragend', (event) => {
            const next = {
              lat: event.latLng.lat(),
              lng: event.latLng.lng(),
              address: `${event.latLng.lat().toFixed(5)}, ${event.latLng.lng().toFixed(5)}`,
              source: 'google_drag'
            };
            onChange(next);
            reverseGeocode(event.latLng, next, onChange);
          });
        }
      } else {
        ref.current.setPosition(position);
        ref.current.setIcon(buildMarkerIcon(google, kind, color, customUrl));
      }
    };

    ensureMarker(pickupMarkerRef, pickup, 'Tu', 'person', '#00F0FF', onPickupChange, markerConfig.pickup);
    ensureMarker(dropoffMarkerRef, dropoff, 'Destino', 'pin', '#EF4444', onDropoffChange, markerConfig.dropoff);

    if (hasPoint(pickup) && hasPoint(dropoff)) return;
    const center = hasPoint(pickup) ? asLatLng(pickup) : hasPoint(dropoff) ? asLatLng(dropoff) : null;
    if (center) map.panTo(center);
  }, [ready, pickup, dropoff, onPickupChange, onDropoffChange, reverseGeocode, markerConfig, readOnly]);

  useEffect(() => {
    if (!ready || !directionsServiceRef.current || !directionsRendererRef.current) return;
    const requestId = routeRequestRef.current + 1;
    routeRequestRef.current = requestId;

    if (!hasPoint(pickup) || !hasPoint(dropoff)) {
      directionsRendererRef.current.set('directions', null);
      if (onRouteChange) onRouteChange(null);
      return;
    }

    setMessage('');
    directionsServiceRef.current.route(
      {
        origin: asLatLng(pickup),
        destination: asLatLng(dropoff),
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false
      },
      (result, status) => {
        if (requestId !== routeRequestRef.current) return;
        if (status !== 'OK' || !result?.routes?.[0]) {
          const distanceKm = Number(haversineKm(pickup, dropoff).toFixed(2));
          const durationMinutes = Math.max(3, Math.round(distanceKm * 3));
          setMessage('No se pudo calcular ruta real. Puedes continuar con distancia estimada.');
          if (onRouteChange) {
            onRouteChange({
              distance_km: distanceKm,
              duration_minutes: durationMinutes,
              pickup_address: pickup.address || 'Origen',
              dropoff_address: dropoff.address || 'Destino',
              provider: 'estimated_google_fallback'
            });
          }
          return;
        }

        directionsRendererRef.current.setDirections(result);
        const route = result.routes[0];
        if (route.bounds) {
          mapInstanceRef.current?.fitBounds(route.bounds, readOnly ? 72 : 56);
        }
        const leg = route.legs?.[0];
        if (!leg) return;
        const distanceKm = Number(((leg.distance?.value || 0) / 1000).toFixed(2));
        const durationMinutes = Math.max(1, Math.round((leg.duration?.value || 0) / 60));
        const encodedPolyline = typeof route.overview_polyline === 'string'
          ? route.overview_polyline
          : route.overview_polyline?.points || null;
        if (onRouteChange) {
          onRouteChange({
            distance_km: distanceKm,
            duration_minutes: durationMinutes,
            polyline: encodedPolyline,
            route_path: (route.overview_path || []).map((point) => ({ lat: point.lat(), lng: point.lng() })),
            pickup_address: leg.start_address || pickup.address || 'Origen',
            dropoff_address: leg.end_address || dropoff.address || 'Destino',
            provider: 'google_maps'
          });
        }
      }
    );
  }, [ready, pickup, dropoff, onRouteChange, readOnly]);

  const useCurrentLocation = () => {
    setActiveTarget('pickup');
    if (!navigator.geolocation) {
      setMessage('Tu navegador no permite ubicacion actual. Puedes buscar o tocar el mapa.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          address: 'Mi ubicacion actual',
          source: 'gps',
          accuracy: position.coords.accuracy
        };
        onPickupChange(next);
        setActiveTarget('dropoff');
        setMessage('Ahora selecciona tu destino.');
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo(asLatLng(next));
          mapInstanceRef.current.setZoom(16);
        }
      },
      () => setMessage('No se pudo obtener ubicacion actual. Puedes buscar o tocar el mapa.'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const handleKeyDown = (target, value) => (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      geocodeText(target, value);
    }
  };

  return (
    <div className="space-y-3">
      {showInputs && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5 min-w-0">
            <button
              type="button"
              onClick={() => setActiveTarget('pickup')}
              className={`text-xs font-medium ${activeTarget === 'pickup' ? 'text-[#00F0FF]' : 'text-[#D1D5DB]'}`}
            >
              Donde estoy
            </button>
            <Input
              ref={pickupInputRef}
              placeholder="Busca o escribe tu recogida"
              value={pickupText}
              onFocus={() => setActiveTarget('pickup')}
              onChange={(e) => setPickupText(e.target.value)}
              onBlur={() => geocodeText('pickup', pickupText)}
              onKeyDown={handleKeyDown('pickup', pickupText)}
              className={`${activeTarget === 'pickup' ? 'border-[#00F0FF]/70 ring-1 ring-[#00F0FF]/20' : 'border-white/10'} bg-white/5 text-white rounded-xl min-h-[46px]`}
            />
          </div>
          <div className="space-y-1.5 min-w-0">
            <button
              type="button"
              onClick={() => setActiveTarget('dropoff')}
              className={`text-xs font-medium ${activeTarget === 'dropoff' ? 'text-[#FF007F]' : 'text-[#D1D5DB]'}`}
            >
              A donde voy
            </button>
            <Input
              ref={dropoffInputRef}
              placeholder="Busca o escribe tu destino"
              value={dropoffText}
              onFocus={() => setActiveTarget('dropoff')}
              onChange={(e) => setDropoffText(e.target.value)}
              onBlur={() => geocodeText('dropoff', dropoffText)}
              onKeyDown={handleKeyDown('dropoff', dropoffText)}
              className={`${activeTarget === 'dropoff' ? 'border-[#FF007F]/70 ring-1 ring-[#FF007F]/20' : 'border-white/10'} bg-white/5 text-white rounded-xl min-h-[46px]`}
            />
          </div>
        </div>
      )}
      {!readOnly && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {showLocationButton && (
            <Button type="button" onClick={useCurrentLocation} className="bg-white/5 border border-white/10 text-white rounded-xl min-h-[44px]">
              <Crosshair className="w-4 h-4 mr-2" /> Usar mi ubicacion actual
            </Button>
          )}
          <span className="text-xs text-[#9CA3AF]">Toca el mapa para fijar {activeTarget === 'pickup' ? 'recogida' : 'destino'}.</span>
        </div>
      )}
      {message && <p className="text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">{message}</p>}
      <div ref={mapRef} style={{ height, width: '100%', borderRadius: '1.5rem', overflow: 'hidden' }} data-testid="google-taxi-map" />
    </div>
  );
};

export default GoogleTaxiMap;
